/**
 * Builds auth-status change comments and stages them in UserStore.
 * @module createDataForCommentsAndPushToLocalStorage
 */

import moment from "moment";
import { AuthStatusChangeComments } from "../../../../data-model/authStatusChangeComments";
import { pushAuthStatusChangeCommentsToLocalStorage } from "../../../../pages/singleOrderLayout/utils/helperForAuthStatusCommentsInLocalStorage";
import {
  PreferredDrugFormState,
  PreferredJcodeEntry,
} from "../statusUpdateModal.types";

/**
 * Creates an authorization status change comment and stores it in UserStore.
 *
 * Maps {@link PreferredDrugFormState} into a snake_case `additional_info`
 * payload for persistence:
 * - `preferredDrugValues` → `preferred_drug_attempted`
 * - `stepTherapyCount` → `step_therapy_count`
 * - `source` → `source`
 * - `preferredJcodeEntries` → `preferred_jcode_entries` (`j_code`, `drug_name`)
 *
 * @input orderId - Order to associate the comment with
 * @input commentText - Comment body (trimmed before save)
 * @input reason - Status-change reason
 * @input jCode - J-code for the CPT row
 * @input commentType - denial, query, query_resolution, or hold
 * @input userId - Commenting user email or id
 * @input additionalInformation - Preferred-drug form state (or empty object)
 *   mapped into `additional_info`
 * @return void
 */
export const createDataForCommentsAndPushToLocalStorage = (
  orderId: string,
  commentText: string,
  reason: string,
  jCode: string,
  commentType: "denial" | "query" | "query_resolution" | "hold",
  userId: string,
  additionalInformation: PreferredDrugFormState | Record<string, unknown> = {},
) => {
  const preferredJcodeEntries = additionalInformation?.preferredJcodeEntries
    ? (additionalInformation.preferredJcodeEntries as PreferredJcodeEntry[])
    : [];
  const comment: AuthStatusChangeComments = {
    comment: commentText.trim(),
    reason: reason ?? "",
    j_code: jCode ?? "",
    comment_type: commentType ?? "",
    commented_by: userId ?? "",
    created_at: moment().utc().format("YYYY-MM-DD HH:mm:ss.SSSSSS+00:00"),
    additional_info: {
      preferred_drug_attempted: additionalInformation.preferredDrugValues,
      step_therapy_count: additionalInformation.stepTherapyCount,
      source: additionalInformation.source,
      preferred_jcode_entries:
        preferredJcodeEntries.map((entry) => {
          return {
            j_code: entry.jCode,
            drug_name: entry.drugName,
          };
        }) ?? [],
    },
  };

  pushAuthStatusChangeCommentsToLocalStorage(orderId, comment);
};
